import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { admin, AdminOverview } from "../../api/client";
import { fmtNum, PageHeader, Stat } from "./common";

function ConfigRow({ label, value, ok }: { label: string; value: string; ok?: boolean }) {
  return (
    <div className="detail-row">
      <span className="label">{label}</span>
      <span className="value">
        {ok !== undefined && (
          <span style={{ marginRight: 6, display: "inline-flex", verticalAlign: "-2px", color: ok ? "var(--success)" : "var(--warning)" }}>
            {ok ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
          </span>
        )}
        {value}
      </span>
    </div>
  );
}

export default function Overview() {
  const [data, setData] = useState<AdminOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = () => admin.overview().then(setData).catch((e) => setError(e.message));
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  if (error) return <div className="page-error">{error}</div>;
  if (!data) return <div className="page-empty">Loading…</div>;
  const m = data.messages_by_status;
  const c = data.config;

  return (
    <div className="page">
      <PageHeader title="System overview" sub="Live view of the mail → AI → QuickBooks pipeline. Refreshes every 15 seconds." />

      <div className="stat-row">
        <Stat label="Users" value={fmtNum(data.users)} />
        <Stat label="Mailboxes" value={fmtNum(data.mailboxes)} />
        <Stat label="Failing mailboxes" value={fmtNum(data.mailboxes_failing)} tone={data.mailboxes_failing ? "bad" : undefined} />
        <Stat label="Receipts (24h / total)" value={`${fmtNum(data.receipts_24h)} / ${fmtNum(data.receipts)}`} />
      </div>

      <h2>Ingest queue</h2>
      <div className="stat-row">
        <Stat label="Waiting for AI" value={fmtNum((m.pending ?? 0) + (m.processing ?? 0))} tone={(m.pending ?? 0) > 50 ? "warn" : undefined} />
        <Stat label="Parsed" value={fmtNum(m.parsed)} />
        <Stat label="Skipped" value={fmtNum(m.skipped)} />
        <Stat label="Errors" value={<Link to="/admin/messages?status=error">{fmtNum(m.error)}</Link>} tone={m.error ? "bad" : undefined} />
        <Stat label="Uploads in progress" value={fmtNum(data.uploads_pending)} />
        <Stat label="Open parse reports" value={<Link to="/admin/reports">{fmtNum(data.open_reports)}</Link>} tone={data.open_reports ? "warn" : undefined} />
      </div>

      <h2>AI usage (last 24h)</h2>
      <div className="stat-row">
        <Stat label="AI calls" value={fmtNum(data.ai_24h.calls)} />
        <Stat label="Input tokens" value={fmtNum(data.ai_24h.input_tokens)} />
        <Stat label="Output tokens" value={fmtNum(data.ai_24h.output_tokens)} />
        <Stat label="Failed / declined" value={fmtNum(data.ai_24h.failures)} tone={data.ai_24h.failures ? "warn" : undefined} />
        <Stat label="Avg latency" value={`${(data.ai_24h.avg_latency_ms / 1000).toFixed(1)}s`} />
      </div>

      <div className="detail-card">
        <h3 style={{ marginTop: 0 }}>Deployment configuration</h3>
        <p className="muted" style={{ marginTop: 0 }}>Set through environment variables / Kubernetes secrets.</p>
        <ConfigRow label="AI provider" value={c.ai_provider} ok={c.ai_provider === "openrouter"} />
        <ConfigRow label="Model / reasoning" value={`${c.ai_model || "not set"} · ${c.ai_reasoning_effort}`} />
        <ConfigRow label="OpenRouter API key" value={c.ai_key_configured ? "configured" : "missing"} ok={c.ai_key_configured || c.ai_provider !== "openrouter"} />
        <ConfigRow label="Gmail OAuth" value={c.gmail_configured ? "configured" : "not configured"} ok={c.gmail_configured} />
        <ConfigRow label="Outlook OAuth" value={c.outlook_configured ? "configured" : "not configured"} ok={c.outlook_configured} />
        <ConfigRow label="QuickBooks" value={`${c.qbo_mode} · ${c.qbo_environment}`} ok={c.qbo_mode === "real"} />
        <ConfigRow label="Public URL" value={c.public_url} />
      </div>
    </div>
  );
}
